import { Injectable } from '@angular/core';
import { EventInput } from '@fullcalendar/core';
import { SchedulerDurationUnit, SchedulerDurationUnitOption, SchedulerFormValue, SchedulerFrequency,
  SchedulerFrequencyOption, SchedulerMonth, SchedulerMonthlyPosition, SchedulerValue,
  SchedulerWeekday
} from './scheduler.interface';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class SchedulerService {

  readonly frequencyOptions: SchedulerFrequencyOption[] = [
    {
      key: 'daily',
      label: 'Daily'
    },
    {
      key: 'weekly',
      label: 'Weekly'
    },
    {
      key: 'monthly',
      label: 'Monthly'
    },
    {
      key: 'yearly',
      label: 'Yearly'
    }
  ];

  readonly weekdays: SchedulerWeekday[] = [
    {
      key: 'MO',
      label: 'Monday',
      short_label: 'Mon'
    },
    {
      key: 'TU',
      label: 'Tuesday',
      short_label: 'Tue'
    },
    {
      key: 'WE',
      label: 'Wednesday',
      short_label: 'Wed'
    },
    {
      key: 'TH',
      label: 'Thursday',
      short_label: 'Thu'
    },
    {
      key: 'FR',
      label: 'Friday',
      short_label: 'Fri'
    },
    {
      key: 'SA',
      label: 'Saturday',
      short_label: 'Sat'
    },
    {
      key: 'SU',
      label: 'Sunday',
      short_label: 'Sun'
    }
  ];

  readonly monthlyPositions: SchedulerMonthlyPosition[] = [
    {
      value: 1,
      label: 'First'
    },
    {
      value: 2,
      label: 'Second'
    },
    {
      value: 3,
      label: 'Third'
    },
    {
      value: 4,
      label: 'Fourth'
    },
    {
      value: -1,
      label: 'Last'
    }
  ];

  readonly monthDays: number[] = Array.from(
    {
      length: 31
    },
    (
      _,
      index
    ) => index + 1
  );

  readonly months: SchedulerMonth[] = [
    {
      value: 1,
      label: 'January'
    },
    {
      value: 2,
      label: 'February'
    },
    {
      value: 3,
      label: 'March'
    },
    {
      value: 4,
      label: 'April'
    },
    {
      value: 5,
      label: 'May'
    },
    {
      value: 6,
      label: 'June'
    },
    {
      value: 7,
      label: 'July'
    },
    {
      value: 8,
      label: 'August'
    },
    {
      value: 9,
      label: 'September'
    },
    {
      value: 10,
      label: 'October'
    },
    {
      value: 11,
      label: 'November'
    },
    {
      value: 12,
      label: 'December'
    }
  ];

  readonly durationUnits: SchedulerDurationUnitOption[] = [
    {
      key: 'minutes',
      label: 'Minutes'
    },
    {
      key: 'hours',
      label: 'Hours'
    },
    {
      key: 'days',
      label: 'Days'
    }
  ];

  constructor() {}

  /**
   * Creates and returns the default form value for a scheduler.
   * The resulting object contains predefined configurations for
   * frequency, interval, start date and time, duration, and other
   * scheduling parameters.
   *
   * @return {SchedulerFormValue} The default form value object for the scheduler.
   */
  createDefaultFormValue(): SchedulerFormValue {
    return {
      frequency: 'weekly',
      interval: 1,

      weekdays: [],

      monthly_mode: 'day_of_month',
      monthly_day: null,
      monthly_days: [],
      monthly_months: [],
      monthly_weekday: null,
      monthly_position: null,

      schedule_start_at: null,
      schedule_end_at: null,

      is_all_day: false,

      duration_value: 1,
      duration_unit: 'hours',
      duration_minutes: 60
    };
  }

  /**
   * Converts a duration in minutes into a structured object containing the value and appropriate time unit.
   * The method determines whether the duration can be represented in days, hours, or remains in minutes.
   *
   * @param {number | null} duration_minutes - The duration in minutes to be converted. A null or non-positive value defaults to minutes with a null value.
   * @return {{ value: number | null, unit: SchedulerDurationUnit }} An object containing the numerical value and its respective time unit ('days', 'hours', or 'minutes').
   */
  getDurationFormValue(duration_minutes: number | null): { value: number | null; unit: SchedulerDurationUnit; } {
    if (!duration_minutes || duration_minutes <= 0) {
      return {
        value: null,
        unit: 'minutes'
      };
    }

    if (duration_minutes % 1440 === 0) {
      return {
        value: duration_minutes / 1440,
        unit: 'days'
      };
    }

    if (duration_minutes % 60 === 0) {
      return {
        value: duration_minutes / 60,
        unit: 'hours'
      };
    }

    return {
      value: duration_minutes,
      unit: 'minutes'
    };
  }

  /**
   * Converts a `SchedulerValue` object or null into a `SchedulerFormValue` object.
   *
   * @param {SchedulerValue | null} value - The input scheduler value to convert. If null, a default form value will be returned.
   * @return {SchedulerFormValue} The converted scheduler form value.
   */
  toSchedulerFormValue(value: SchedulerValue | null): SchedulerFormValue {
    const form = this.createDefaultFormValue();

    if (!value) {
      return form;
    }

    const duration = this.getDurationFormValue(
      value.duration_minutes
    );

    return {
      ...form,

      ...this.parseRRule(value.rrule),

      schedule_start_at: value.schedule_start_at,
      schedule_end_at: value.schedule_end_at,

      is_all_day: !!value.is_all_day,

      duration_value: duration.value,
      duration_unit: duration.unit,
      duration_minutes: value.duration_minutes
    };
  }

  /**
   * Converts a `SchedulerFormValue` object into a `SchedulerValue` object.
   *
   * @param {SchedulerFormValue} form - The input form containing scheduling data, such as start date, end date, and recurrence details.
   * @return {SchedulerValue} A transformed object containing processed scheduling parameters like all-day status, duration, recurrence rules, and timestamps for start and end dates.
   */
  toSchedulerValue(form: SchedulerFormValue): SchedulerValue {
    const duration_minutes = form.is_all_day
        ? null
        : this.calculateDurationMinutes(form.duration_value, form.duration_unit);

    return {
      is_all_day: form.is_all_day,
      duration_minutes: duration_minutes,
      rrule: this.buildRRule(form),
      schedule_start_at: form.schedule_start_at,
      schedule_end_at: form.schedule_end_at
    };
  }

  /**
   * Builds a recurrence rule (RRule) string based on the provided scheduler form value.
   *
   * @param {SchedulerFormValue} form - An object containing scheduling details such as frequency, interval, weekdays, monthly mode, and related properties.
   * @return {string} A string representation of the recurrence rule adhering to the iCalendar RFC 5545 standard.
   */
  buildRRule(form: SchedulerFormValue): string {
    const parts: string[] = [];

    parts.push(
      `FREQ=${form.frequency.toUpperCase()}`
    );

    const usesSpecificMonths = form.frequency === 'monthly' && form.monthly_mode === 'multiple_days_months';

    if (!usesSpecificMonths && form.interval && form.interval > 1) {
      parts.push(
        `INTERVAL=${form.interval}`
      );
    }

    if (form.frequency === 'weekly' && form.weekdays.length) {
      parts.push(
        `BYDAY=${form.weekdays.join(',')}`
      );
    }

    if (form.frequency === 'monthly' && form.monthly_mode === 'day_of_month' && form.monthly_day) {
      parts.push(
        `BYMONTHDAY=${form.monthly_day}`
      );
    }

    if (form.frequency === 'monthly' && form.monthly_mode === 'multiple_days' && form.monthly_days.length) {
      parts.push(
        `BYMONTHDAY=${form.monthly_days.join(',')}`
      );
    }

    if (form.frequency === 'monthly' && form.monthly_mode === 'multiple_days_months' && form.monthly_days.length && form.monthly_months.length) {
      parts.push(
        `BYMONTH=${form.monthly_months.join(',')}`
      );

      parts.push(
        `BYMONTHDAY=${form.monthly_days.join(',')}`
      );
    }

    if (form.frequency === 'monthly' && form.monthly_mode === 'weekday' && form.monthly_weekday && form.monthly_position) {
      parts.push(
        `BYDAY=${form.monthly_weekday}`
      );

      parts.push(
        `BYSETPOS=${form.monthly_position}`
      );
    }

    if (form.frequency === 'monthly' && form.monthly_mode === 'last_day_of_month') {
      parts.push(
        'BYMONTHDAY=-1'
      );
    }

    return parts.join(';');
  }

  /**
   * Parses a recurrence rule (RRULE) string and converts it into a partial SchedulerFormValue object.
   *
   * @param {string | null} rrule - The recurrence rule string to be parsed. If null, an empty object is returned.
   * @return {Partial<SchedulerFormValue>} An object containing parsed RRULE data as key-value pairs. The object includes properties such as frequency, interval, weekdays, and monthly mode details, depending on the contents of the RRULE string.
   */
  parseRRule(rrule: string | null): Partial<SchedulerFormValue> {
    if (!rrule) {
      return {};
    }

    const parts: Record<string, string> = {};

    rrule
      .split(';')
      .forEach(
        part => {
          const [key, value] = part.split('=');

          if (key && value) {
            parts[key.toUpperCase()] = value;
          }
        }
      );

    const result: Partial<SchedulerFormValue> = {};

    if (parts['FREQ']) {
      result.frequency = parts['FREQ'].toLowerCase() as SchedulerFrequency;
    }

    result.interval = parts['INTERVAL'] ? Number(parts['INTERVAL']) : 1;

    if (result.frequency === 'weekly') {
      result.weekdays = parts['BYDAY'] ? parts['BYDAY'].split(',') : [];
    }

    if (result.frequency === 'monthly') {
      if (parts['BYMONTH'] && parts['BYMONTHDAY']) {
        result.monthly_mode = 'multiple_days_months';

        result.monthly_months = parts['BYMONTH'].split(',').map(
          value => Number(value)
        );

        result.monthly_days = parts['BYMONTHDAY'].split(',').map(
          value => Number(value)
        );
      } else if (parts['BYMONTHDAY']) {
        const days = parts['BYMONTHDAY'].split(',').map(
          value => Number(value)
        );

        if (days.length === 1 && days[0] === -1) {
          result.monthly_mode = 'last_day_of_month';
        } else if (days.length > 1) {
          result.monthly_mode = 'multiple_days';
          result.monthly_days = days;
        } else {
          result.monthly_mode = 'day_of_month';
          result.monthly_day = days[0] || null;
        }
      } else if (parts['BYDAY'] && parts['BYSETPOS']) {
        result.monthly_mode = 'weekday';
        result.monthly_weekday = parts['BYDAY'];
        result.monthly_position = Number(parts['BYSETPOS']);
      }
    }

    return result;
  }

  /**
   * Determines the ordinal suffix for a given number and returns the number with its appropriate suffix.
   *
   * @param {number} value - The number for which the ordinal suffix needs to be determined.
   * @return {string} The number concatenated with its appropriate ordinal suffix (e.g., 1st, 2nd, 3rd, 4th).
   */
  private getOrdinal(value: number): string {
    const remainder100 = value % 100;

    if (remainder100 >= 11 && remainder100 <= 13) {
      return `${value}th`;
    }

    switch (value % 10) {
      case 1:
        return `${value}st`;

      case 2:
        return `${value}nd`;

      case 3:
        return `${value}rd`;

      default:
        return `${value}th`;
    }
  }

  /**
   * Generates a summary of the given duration in minutes, converting it to hours and minutes as appropriate.
   *
   * @param {number | null | undefined} duration_minutes - The duration in minutes to be summarized.
   * @return {string} A string representing the duration in a human-readable format (e.g., "1 hour 30 minutes" or "45 minutes").
   */
  public getDurationSummary(duration_minutes: number | null | undefined): string {
    if (!duration_minutes || duration_minutes <= 0) {
      return '';
    }

    if (duration_minutes < 60) {
      return `${duration_minutes} minute${duration_minutes === 1 ? '' : 's'}`;
    }

    if (duration_minutes >= 1440 && duration_minutes % 1440 === 0) {
      const days = duration_minutes / 1440;

      return `${days} day${days === 1 ? '' : 's'}`;
    }

    const days = Math.floor(duration_minutes / 1440);

    const remaining_minutes = duration_minutes % 1440;

    const hours = Math.floor(remaining_minutes / 60);

    const minutes = remaining_minutes % 60;

    const parts: string[] = [];

    if (days > 0) {
      parts.push(`${days} day${days === 1 ? '' : 's'}`);
    }

    if (hours > 0) {
      parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
    }

    if (minutes > 0) {
      parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
    }

    return parts.join(' ');
  }

  /**
   * Generates a summary string based on the frequency and interval values from the provided form input.
   *
   * @param {SchedulerFormValue} form - The form input containing scheduling information.
   * @param {string} timezone - The timezone to use for formatting the schedule summary.
   * This object includes properties such as frequency, interval, weekdays, and other relevant scheduling data.
   * @return {string} A summary string describing the schedule in a human-readable format.
   * The summary varies depending on the frequency (daily, weekly, monthly, yearly) and other form details.
   */
  getSummary(form: SchedulerFormValue, timezone: string): string {
    const interval = form.interval || 1;

    let summary = '';

    switch (form.frequency) {

      case 'daily':summary = interval === 1
        ? 'Every day'
        : `Every ${interval} days`;

        break;

      case 'weekly':
        summary = interval === 1
          ? 'Every week'
          : `Every ${interval} weeks`;

        if (form.weekdays.length) {
          const labels = form.weekdays
              .map(
                key =>
                  this.weekdays.find(
                    weekday => weekday.key === key
                  )?.short_label
              )
              .filter(Boolean);

          if (labels.length) {
            summary += ` on ${labels.join(', ')}`;
          }
        }

        break;

      case 'monthly':
        // Multiple Days and Months does not use the Repeat Every / interval value.
        if (form.monthly_mode === 'multiple_days_months') {
          const months =
            [...form.monthly_months].sort(
              (a, b) => a - b
            )
            .map(
              value => this.months.find(
                month => month.value === value
              )?.label
            )
            .filter(Boolean);

          const days =
            [...form.monthly_days].sort(
              (a, b) => a - b
            )
            .map(day => this.getOrdinal(day));

          summary = `Every ${months.join(', ')} on the ${days.join(', ')}`;

          break;
        }

        summary = interval === 1
          ? 'Every month'
          : `Every ${interval} months`;

        if (form.monthly_mode === 'day_of_month' && form.monthly_day) {
          summary += ` on the ${this.getOrdinal(form.monthly_day)}`;
        }

        if (form.monthly_mode === 'multiple_days' && form.monthly_days.length) {
          const days = [...form.monthly_days].sort(
            (a, b) => a - b
          )
          .map(day => this.getOrdinal(day));

          summary += ` on the ${days.join(', ')}`;
        }

        if (form.monthly_mode === 'weekday' && form.monthly_weekday && form.monthly_position) {
          const weekday = this.weekdays.find(
            item => item.key === form.monthly_weekday
          );

          const position = this.monthlyPositions.find(
            item => item.value === form.monthly_position
          );

          if (weekday && position) {
            summary += ` on the ${position.label.toLowerCase()} ${weekday.label}`;
          }
        }

        if (form.monthly_mode === 'last_day_of_month') {
          summary += ' on the last day of the month';
        }

        break;

      case 'yearly':
        summary = interval === 1
          ? 'Every year'
          : `Every ${interval} years`;

        break;
    }

    if (form.is_all_day) {
      summary += ' — All Day';
      return summary;
    }

    if (form.schedule_start_at) {
      const start = moment
        .unix(form.schedule_start_at)
        .tz(timezone);

      summary += ` at ${start.format('h:mm A')}`;
    }

    if (form.duration_minutes && form.duration_minutes > 0) {
      summary += ` for ${this.getDurationSummary(form.duration_minutes)}`;
    }

    return summary;
  }


  /**
   * Builds a preview event object based on the provided form data and scheduling parameters.
   *
   * @param {SchedulerFormValue} form - The scheduling form values containing information such as start time, end time, recurrence rules, and other configurations.
   * @param {string} timezone - The timezone to be used for formatting and interpreting date and time values.
   * @param {string} [title='Scheduled Event'] - The title of the event to be displayed. Defaults to 'Scheduled Event' if not provided.
   * @return {EventInput | null} Returns an event object compatible with FullCalendar or null if the required start time is not present.
   */
  buildPreviewEvent(form: SchedulerFormValue, timezone: string, title: string = 'Scheduled Event'): EventInput | null {
    if (!form.schedule_start_at) {
      return null;
    }

    const start = moment.unix(form.schedule_start_at).tz(timezone);

    const end = form.schedule_end_at
        ? moment.unix(form.schedule_end_at).tz(timezone)
        : null;

    const rrule: Record<string, any> = {
      freq: form.frequency,
      dtstart: form.is_all_day
        ? start.format('YYYY-MM-DD')
        : start.format('YYYY-MM-DDTHH:mm:ss')
    };

    if (!(form.frequency === 'monthly' && form.monthly_mode === 'multiple_days_months')) {
      rrule['interval'] = form.interval || 1;
    }

    if (form.frequency === 'weekly' && form.weekdays.length) {
      rrule['byweekday'] = form.weekdays.map(
        weekday => weekday.toLowerCase()
      );
    }

    if (
      form.frequency === 'monthly' &&
      form.monthly_mode === 'day_of_month' &&
      form.monthly_day
    ) {
      rrule['bymonthday'] = form.monthly_day;
    }

    if (
      form.frequency === 'monthly' &&
      form.monthly_mode === 'multiple_days' &&
      form.monthly_days.length
    ) {
      rrule['bymonthday'] = form.monthly_days;
    }

    if (
      form.frequency === 'monthly' &&
      form.monthly_mode === 'multiple_days_months' &&
      form.monthly_days.length &&
      form.monthly_months.length
    ) {
      rrule['bymonth'] = form.monthly_months;
      rrule['bymonthday'] = form.monthly_days;
    }

    if (
      form.frequency === 'monthly' &&
      form.monthly_mode === 'weekday' &&
      form.monthly_weekday &&
      form.monthly_position
    ) {
      rrule['byweekday'] = [form.monthly_weekday.toLowerCase()];
      rrule['bysetpos'] = form.monthly_position;
    }

    if (
      form.frequency === 'monthly' &&
      form.monthly_mode === 'last_day_of_month'
    ) {
      rrule['bymonthday'] = -1;
    }

    // Used only by FullCalendar so the visual preview stops at the selected schedule end.
    if (end) {
      rrule['until'] = form.is_all_day
        ? end.format('YYYY-MM-DD')
        : end.format('YYYY-MM-DDTHH:mm:ss');
    }

    const event: EventInput = {
      title,
      allDay: form.is_all_day,
      rrule
    };

    if (
      !form.is_all_day &&
      form.duration_minutes &&
      form.duration_minutes > 0
    ) {
      event.duration = {
        minutes: form.duration_minutes
      };
    }

    return event;
  }

  /**
   * Calculates the duration in minutes based on the input value and the specified unit of time.
   *
   * @param {number | null} value - The numerical value representing the duration. Must be greater than 0 or null.
   * @param {SchedulerDurationUnit} unit - The unit of time for the given value. Supported units: 'minutes', 'hours', 'days'.
   * @return {number | null} The calculated duration in minutes, or null if the input value is invalid or the unit is unsupported.
   */
  calculateDurationMinutes(value: number | null, unit: SchedulerDurationUnit): number | null {
    if (value === null || value <= 0) {
      return null;
    }

    switch (unit) {
      case 'minutes':
        return value;

      case 'hours':
        return value * 60;

      case 'days':
        return value * 1440;

      default:
        return null;
    }
  }

}
